import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Hard-coded fallback that matches Earth Tone — used only when no curated
// theme catalog is reachable yet (e.g. cold network, super-admin without
// tenant context).
const DEFAULT = {
  primary_bg: "#E7D7C8",
  secondary_bg: "#e2c8b5",
  primary_text: "#2f2e2c",
  secondary_text: "#783D19",
  accent: "#2e630c",
  accent_hover: "#2e630c",   // legacy alias (free-form branding used this key)
  warning: "#f97316",
  error: "#ef4444",
  heading_font: "Fraunces",
  body_font: "Catamaran",
};

// Map every palette source onto the CSS variables the app actually consumes.
function applyPalette(p) {
  const r = document.documentElement;
  r.style.setProperty("--vb-bg",       p.primary_bg     || DEFAULT.primary_bg);
  r.style.setProperty("--vb-bg-2",     p.secondary_bg   || DEFAULT.secondary_bg);
  r.style.setProperty("--vb-text",     p.primary_text   || DEFAULT.primary_text);
  r.style.setProperty("--vb-text-2",   p.secondary_text || DEFAULT.secondary_text);
  // Curated themes expose `accent`; legacy free-form branding used `accent_hover`.
  r.style.setProperty("--vb-accent",   p.accent || p.accent_hover || DEFAULT.accent);
  r.style.setProperty("--vb-warning",  p.warning || DEFAULT.warning);
  r.style.setProperty("--vb-error",    p.error || DEFAULT.error);
  if (p.heading_font) r.style.setProperty("--vb-heading-font", p.heading_font);
  if (p.body_font)    r.style.setProperty("--vb-body-font", p.body_font);
}

export default function ThemeProvider({ children }) {
  const { tenant } = useAuth();
  // Cache the curated theme catalog so we can resolve theme_id → palette
  // without an extra fetch on every tenant refresh.
  const [themes, setThemes] = useState([]);

  // Pull the curated catalog once per session (it's small — at most a handful
  // of themes — and the same for every tenant).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/themes");
        if (mounted) setThemes(data.themes || []);
      } catch (e) { /* not signed in / network — fall through to DEFAULT */ }
    })();
    return () => { mounted = false; };
  }, [tenant?.id]);

  useEffect(() => {
    // Resolution order:
    //   1. Curated theme picked via Settings → Brand Theme (tenant.theme_id)
    //   2. Legacy free-form `tenant.branding` palette (kept so older tenants
    //      who haven't migrated still render their colours)
    //   3. Hard-coded DEFAULT
    const curated = tenant?.theme_id && themes.find((t) => t.id === tenant.theme_id);
    if (curated) {
      applyPalette({
        ...curated.palette,
        heading_font: tenant?.branding?.heading_font,
        body_font: tenant?.branding?.body_font,
      });
      return;
    }
    if (tenant?.branding && Object.keys(tenant.branding).length > 0) {
      applyPalette({ ...DEFAULT, ...tenant.branding });
      return;
    }
    applyPalette(DEFAULT);
  }, [tenant, themes]);

  return children;
}
